import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { Button } from 'react-bootstrap';
import { AiOutlineClose } from 'react-icons/ai';
import { t } from 'i18next';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import ButtonObelisk from '../components/ui/ButtonComponent';
import { cacheGet } from '../util/cache';

export const ModalContext = createContext();

/**
 * @settings ModalSettings {
 *            disableClose: boolean,
 *            centered: boolean,
 *            size: 'sm' | 'md' | 'lg' | 'xl',
 *            padContent: boolean,
 *            footer: node
 *          }
 * @returns {object} {
            setContent,
            setTitle,
            setOnLoadEvent,
            setOnCloseEvent,
            showModal,
            hideModal,
            setModalSettings,
            modalStatus
        }
 */
export const useModal = () => {
  if (!ModalContext) {
    toast.error('ModalContext is not defined');
    throw new Error('useModal must be used within a ModalProvider');
  }
  return useContext(ModalContext);
};

const iniSettings = {
  disableClose: false,
  centered: false,
  size: 'md',
  padContent: true,
};

const ModalProvider = ({ children }) => {
  const [onLoadEvent, setOnLoadEvent] = useState(() => () => null);
  const [onCloseEvent, setOnCloseEvent] = useState(() => () => null);
  const [content, setContent] = useState(<></>);
  const [title, setTitle] = useState('');
  const [modalSettings, setModalSettings] = useState(iniSettings);
  const [toggleModal, setToggleModal] = useState(false);
  const [fader, setFader] = useState(undefined);

  const resetModal = useCallback(() => {
    setContent(<></>);
    setTitle('');
    setModalSettings(iniSettings);
    setOnCloseEvent(() => () => {});
    onLoadEvent(() => () => {});
    setToggleModal(false);
    setFader(undefined);
  }, []);

  useEffect(() => {
    if (toggleModal) {
      if (typeof onLoadEvent === 'function') {
        onLoadEvent();
      }
      const fadeEffect = setTimeout(() => {
        setFader('show');
      }, 150);
      return () => {
        clearTimeout(fadeEffect);
        const removeFader = setTimeout(() => {
          setToggleModal(false);
          if (typeof onCloseEvent === 'function') onCloseEvent();
        }, 150);
        resetModal();
      };
    }
  }, [toggleModal]);

  // Set transition timing
  const transition = '.35s ease-in-out';

  const showModal = () => setToggleModal(true);

  const hideModal = () => setToggleModal(false);

  const modalStatus = toggleModal ? 'active' : 'inactive';

  return (
    <ModalContext.Provider
      value={{
        setContent,
        setTitle,
        setOnLoadEvent,
        setOnCloseEvent,
        showModal,
        hideModal,
        setModalSettings,
        modalStatus,
      }}
    >
      {children}
      {toggleModal && (
        <ModalView
          size={modalSettings?.size}
          fader={fader}
          centered={modalSettings?.centered}
          disableClose={modalSettings?.disableClose}
          onCloseEvent={onCloseEvent}
          title={title}
          padContent={modalSettings?.padContent}
          transition={transition}
          footer={modalSettings?.footer}
        >
          {content}
        </ModalView>
      )}
    </ModalContext.Provider>
  );
};

ModalProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ModalProvider;

export const ModalView = ({
  children,
  fader,
  centered,
  disableClose,
  onCloseEvent,
  title,
  padContent,
  footer,
  size = 'lg',
  overflowY = 'auto',
  transition = 'all 0.3s ease-in-out',
}) => {
  const theme = useTheme();
  return (
    <>
      <Box
        className={`modal modal-${size || 'md'} ${fader || ''} modal-blur mui-mode-${cacheGet('setting.theme.mode', false, theme.palette.mode)}`}
        tabIndex='-1'
        role='dialog'
        sx={{
          display: 'block',
          position: 'fixed',
          pointerEvents: 'none',
          zIndex: 1501,
          boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)',
          borderColor: 'var(--tblr-border-color)',
          transition,
          opacity: fader === 'show' ? 1 : 0,
        }}
        aria-modal='true'
      >
        <Box
          className={`modal-dialog ${centered ? 'modal-dialog-centered' : ''}`}
          role='document'
          // onClick={e => e.stopPropagation()}
          sx={{
            height: 'calc(100vh - 4em)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            transition,
            transform: `translateY(${fader === 'show' ? '0' : '-30px'})`,
            pointerEvents: 'none',
          }}
        >
          <Box
            className='modal-content'
            sx={{
              pointerEvents: fader === 'show' ? 'auto' : 'none',
            }}
          >
            {!disableClose && (
              <Button
                aria-label={t('Close')}
                onClick={() => {
                  if (typeof onCloseEvent === 'function') onCloseEvent();
                }}
                variant='text'
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 'auto',
                  padding: '1em',
                  textDecoration: 'none',
                  position: 'absolute',
                  top: '5px',
                  right: '5px',
                  zIndex: 2,
                  border: 'none',
                  boxShadow: 'none',
                  pointerEvents: fader === 'show' ? 'initial' : 'none',
                }}
              >
                <AiOutlineClose size={20} />
              </Button>
            )}
            {title && typeof title === 'string' && (
              <Typography
                variant='h6'
                style={{
                  fontSize: '1rem',
                  margin: '16px',
                  textAlign: 'left',
                  pointerEvents: fader === 'show' ? 'initial' : 'none',
                }}
              >
                {title}
              </Typography>
            )}
            {title && typeof title !== 'string' && (
              <Box
                style={{
                  margin: '16px',
                  textAlign: 'left',
                  pointerEvents: fader === 'show' ? 'initial' : 'none',
                }}
              >
                {title}
              </Box>
            )}
            <Box
              className={padContent ? 'm-3' : ''}
              sx={{
                maxHeight: 'calc(96vh - 130px)',
                overflowY: overflowY,
                pointerEvents: fader === 'show' ? 'initial' : 'none',
              }}
            >
              {children}
            </Box>
            {footer && (
              <Box
                className='modal-footer'
                sx={{
                  pointerEvents: fader === 'show' ? 'initial' : 'none',
                }}
              >
                {footer}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      <Box
        className={`modal-backdrop ${fader || ''} modal-blur`}
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          transition,
          zIndex: 1500,
          opacity: fader === 'show' ? 0.24 : 0,
          pointerEvents: fader === 'show' ? 'auto' : 'none',
        }}
        onClick={() => {
          if (typeof onCloseEvent === 'function') onCloseEvent();
        }}
      ></Box>
    </>
  );
};

ModalView.propTypes = {
  children: PropTypes.node.isRequired,
  footer: PropTypes.node,
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
  fader: PropTypes.oneOf(['show', 'hide']),
  centered: PropTypes.bool,
  disableClose: PropTypes.bool,
  onCloseEvent: PropTypes.func,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  padContent: PropTypes.bool,
  transition: PropTypes.string,
  overflowY: PropTypes.string,
};

export const ModalBodyContent = ({ children, label }) => (
  <Box m={3}>
    {label && <Typography variant='body1'>{label}</Typography>}
    {children || <></>}
  </Box>
);

ModalBodyContent.propTypes = {
  children: PropTypes.node,
  label: PropTypes.string,
};

export const ModalFooterContent = ({
  children,
  onCancelEvent,
  onSubmitEvent,
  position = 'before',
  className = 'o-grid col-count-2 gapped col-c1-md my-3',
}) => (
  <Box display={'flex'} justifyContent='center' mt={2} className='w-100'>
    <Box className={className}>
      {position === 'before' && children}
      {typeof onCancelEvent === 'function' && (
        <Box>
          <ButtonObelisk m={0} variant='outlined' onClick={onCancelEvent}>
            {t('cancel')}
          </ButtonObelisk>
        </Box>
      )}
      {typeof onSubmitEvent === 'function' && (
        <Box>
          <ButtonObelisk m={0} variant='contained' color='error' onClick={onSubmitEvent}>
            {t('delete')}
          </ButtonObelisk>
        </Box>
      )}
      {position === 'after' && children}
    </Box>
  </Box>
);

ModalFooterContent.propTypes = {
  children: PropTypes.node,
  onCancelEvent: PropTypes.func,
  onSubmitEvent: PropTypes.func,
  position: PropTypes.string,
  className: PropTypes.string,
};
